import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

const ModularUnitForm = ({ open, onClose, projectId, modularUnit, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    displayOrder: 0
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  
  useEffect(() => {
    if (modularUnit) {
      setFormData({
        name: modularUnit.name || '',
        description: modularUnit.description || '',
        displayOrder: modularUnit.displayOrder || 0
      });
    } else {
      setFormData({
        name: '',
        description: '',
        displayOrder: 0
      });
    }
    setErrors({});
  }, [modularUnit, open]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('NAMA UNIT BANGUNAN WAJIB DIISI');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (modularUnit) {
        // Update existing modular unit
        await api.put(`/modular-units/${modularUnit.id}`, formData);
        enqueueSnackbar(t('UNIT MODULAR BERHASIL DIPERBARUI'), { variant: 'success' });
      } else {
        // Create new modular unit
        await api.post(`/projects/${projectId}/modular-units`, formData);
        enqueueSnackbar(t('UNIT MODULAR BERHASIL DITAMBAHKAN'), { variant: 'success' });
      }
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving modular unit:', error);
      enqueueSnackbar(t('GAGAL MENYIMPAN UNIT BANGUNAN'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {modularUnit ? t('EDIT UNIT BANGUNAN') : t('TAMBAH UNIT BANGUNAN BARU')}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label={t('NAMA UNIT BANGUNAN *')}
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          margin="dense"
          name="description"
          label={t('DESKRIPSI')}
          type="text"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={formData.description}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="dense"
          name="displayOrder"
          label={t('URUTAN TAMPILAN')}
          type="number"
          fullWidth
          variant="outlined"
          value={formData.displayOrder}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('BATAL')}</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('SIMPAN')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModularUnitForm;
